import { useEffect, useState, useMemo, useRef } from 'react';

// icons
import { ReactComponent as InfoIcon } from '../../../assets/icons/imported/info.svg';
import { ReactComponent as FlagIcon } from '../../../assets/icons/imported/finish.svg';
import { ReactComponent as DownArrowIcon } from '../../../assets/icons/imported/down-arrow.svg';
import { ReactComponent as MarketCapIconFilled } from '../../../assets/icons/imported/market-filled.svg';
// translations
import { FormattedMessage } from 'react-intl';

// redux
import {
  setSelectCurrencyModalOpen,
  setSelectCurrencyModalData
} from '../../modals/select-currency/redux/selectCurrencyModalSlice';
import { _getBalances } from '../../../redux/actions';
import { setBalances } from '../../../redux/slices/balancesSlice';

// helpers
import { estimateUnitValue } from '../../helpers/estimateValueBase';
import { setSnackbarData, setSnackbarOpen } from '../../snackbar/redux/snackbarSlice'
import { startPolling } from '../../../redux/slices/orderPollingSlice';
import { formatNumber } from '../../helpers/formatNumber';
import { API } from '../../../utility/API';
// styles
import Styles from './quickBuySell.module.css';

// components
import Button from '../../../components/Button/Button';
import ModernCurrencyInput from '../../../components/ModernCurrencyInput/ModernCurrencyInput';
import RadioButton from '../../../components/RadioButton/RadioButton';
import { _addOrder, _getCurrencies } from '../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import CoinTag from '../../../components/CoinTag/CoinTag';

function QuickBuySell({
  selectedSide = 'buy', // buy - sell
  hideTitle,
  containerStyle = {}
}) {
  const [quantity, setQuantity] = useState('')
  const [apiCurrencies, setApiCurrencies] = useState([])
  const [selectedTab, setSelectedTab] = useState(selectedSide)
  const [selectedRadioButton, setSelectedRadioButton] = useState('market')
  const [price, setPrice] = useState('')
  const [priceLimit, setPriceLimit] = useState('')
  const selectedCoin = useSelector(state => state.selectedCoin.value)
  const marketsList = useSelector(state => state.marketList.value)
  const marketTickers = useSelector(state => state.marketTickers.value)
  const currencies = useSelector(state => state.currencies.value)
  const isLoggedIn = useSelector(state => state.loggedIn.value)
  const dispatch = useDispatch()
  const balances = useSelector(state => state.balances.value)
  const selectCurrencyModalData = useSelector(state => state.selectCurrencyModalState.data)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [refreshBalance, setRefreshBalance] = useState(false)
  const [totalPrice, setTotalPrice] = useState('')
  const [convertedPrice, setConvertedPrice] = useState('')
  const [submitted, setSubmitted] = useState(false)

  // Fee calculation states
  const [fee, setFee] = useState(null)
  const [feeLoading, setFeeLoading] = useState(false)
  const [feeCalculationTimer, setFeeCalculationTimer] = useState(null)

  //quick buy/sell coin
  // const [quantityCurrency, setQuantityCurrency] = useState(marketsList.find(el => el.id === selectedCoin.id)?.quote_unit)
  // const [buyForCurrency, setBuyForCurrency] = useState(useSelector(state => state.currency.value))

  // convert coin
  const [toCurrency, setToCurrency] = useState(
    selectCurrencyModalData?.defaultSelectedMarket?.quote_unit || marketsList?.find(el => el.id === selectedCoin.id)?.quote_unit
  )
  const [fromCurrency, setFromCurrency] = useState(
    selectCurrencyModalData?.defaultSelectedMarket?.base_unit || marketsList?.find(el => el.id === selectedCoin.id)?.base_unit
  )
  const [currencySide, setCurrencySide] = useState('fromCurrency')

  // The pair actually shown in the form. fromCurrency/toCurrency are updated when a
  // market is picked in the select-pair modal, but selectedCoin (redux) is not, so
  // deriving the market from the displayed currencies keeps the API payload in sync
  // with the UI. selectedCoin is only a fallback for the initial render.
  const activeMarket = useMemo(() => {
    if (fromCurrency && toCurrency) {
      const from = String(fromCurrency).toLowerCase()
      const to = String(toCurrency).toLowerCase()
      const match = marketsList?.find(
        el => String(el.base_unit).toLowerCase() === from && String(el.quote_unit).toLowerCase() === to
      )
      if (match) return match

      // The displayed pair has no entry in marketsList. Falling back to selectedCoin
      // here is what sent a SOL/EUR order to btcusd, so resolve to nothing instead and
      // let the callers block rather than trade the wrong market.
      if (marketsList?.length) {
        console.warn('[QuickTrade] displayed pair not found in marketsList', {
          from, to, marketsListSize: marketsList.length
        })
      }
      return null
    }

    // Initial render, before a pair has been resolved from the modal or defaults.
    return marketsList?.find(el => el.id === selectedCoin?.id) || null
  }, [marketsList, fromCurrency, toCurrency, selectedCoin?.id])

  const activeMarketId = activeMarket?.id

  // allowed_to_trade as reported by estimate_fee. It is an object keyed by currency
  // (e.g. { eur: '...', trx: '...' }) but older responses returned a bare number, so
  // both shapes are handled. Buy side is capped in the quote currency, sell side in
  // the base currency. Returns null on a genuinely absent field so a missing key does
  // not read as 0 and falsely disable the button.
  const readAllowedToTrade = (feeData, currency, fallbackKey) => {
    if (!feeData || feeData.allowed_to_trade === undefined || feeData.allowed_to_trade === null) return null

    const allowed = feeData.allowed_to_trade
    if (typeof allowed === 'object') {
      const raw = allowed[currency] ?? (fallbackKey ? allowed[fallbackKey] : undefined)
      if (raw === undefined || raw === null) return null
      const parsed = parseFloat(raw)
      return Number.isFinite(parsed) ? parsed : null
    }

    const parsed = parseFloat(allowed)
    return Number.isFinite(parsed) ? parsed : null
  }

  // Buy spends the quote currency; sell spends the base currency.
  const getAllowedToTrade = () => {
    if (selectedTab === 'buy') return readAllowedToTrade(fee, toCurrency, 'usd')
    return readAllowedToTrade(fee, fromCurrency, activeMarket?.base_unit)
  }

  // Balance validation (after currency states are declared)
  const isAmountExceedingBalance = useMemo(() => {
    if (!isLoggedIn || !balances) return false

    const currencyToCheck = selectedTab === 'buy' ? toCurrency : fromCurrency
    const balance = balances.find(el => el.currency === currencyToCheck)?.balance || 0

    let amountToCheck = 0
    if (selectedTab === 'buy') {
      // For buying: check if total price exceeds quote currency balance
      if (selectedRadioButton === 'limit') {
        amountToCheck = parseFloat(quantity || 0) * parseFloat(priceLimit || 0)
      } else {
        // For market orders: use manually entered price, or auto-calculated convertedPrice
        amountToCheck = parseFloat(price || convertedPrice || 0)
      }
    } else {
      // For selling: check if quantity exceeds base currency balance
      amountToCheck = parseFloat(quantity || 0)
    }

    return parseFloat(balance) < amountToCheck
  }, [balances, selectedTab, quantity, priceLimit, price, convertedPrice, selectedRadioButton, isLoggedIn, toCurrency, fromCurrency])

  // allowed_to_trade validation, mirroring the exchange form. The plain balance check
  // above ignores the fee and the backend's funds lock, so an amount at the wallet
  // balance passes it and is then rejected by the exchange.
  const isAmountExceedsAllowedLimit = useMemo(() => {
    const allowed = getAllowedToTrade()
    if (allowed === null) return false

    let amountToCheck = 0
    if (selectedTab === 'buy') {
      // Buying spends the quote currency
      amountToCheck = selectedRadioButton === 'limit'
        ? parseFloat(quantity || 0) * parseFloat(priceLimit || 0)
        : parseFloat(price || convertedPrice || 0)
    } else {
      // Selling spends the base currency
      amountToCheck = parseFloat(quantity || 0)
    }

    if (!amountToCheck) return false

    return amountToCheck > allowed
  }, [fee, selectedTab, selectedRadioButton, quantity, priceLimit, price, convertedPrice, toCurrency, fromCurrency, activeMarket])

  // Mirrors isBuyButtonDisabled / isSellButtonDisabled on the exchange form, plus the
  // unresolved-market guard. hasNoAmount matters because parseFloat('') is NaN and
  // every NaN comparison is false, so an empty form otherwise reaches _addOrder.
  const isOrderBlocked = useMemo(() => {
    const hasMinAmountError = submitted &&
      parseFloat(quantity || convertedPrice || 0) < parseFloat(activeMarket?.min_amount || 0)

    const hasNoAmount = !quantity && !convertedPrice && !price

    return hasMinAmountError || isAmountExceedingBalance || isAmountExceedsAllowedLimit || hasNoAmount || !activeMarketId
  }, [submitted, quantity, convertedPrice, price, activeMarket, activeMarketId, isAmountExceedingBalance, isAmountExceedsAllowedLimit])

  // redux
  const userData = useSelector(state => state.userData?.value)

  // Helper function to format number to up to 10 decimal places, removing trailing zeros
  const formatToTenDecimals = (number) => {
    if (!number) return '0'
    const num = parseFloat(number)
    return num.toFixed(10).replace(/\.?0+$/, '');
  }

  // Helper function to calculate actual fee based on user input amount
  const calculateActualFee = () => {
    if (!fee || !fee.fee_percentage) return '0.00'

    let userAmount = 0
    if (selectedTab === 'buy') {
      if (selectedRadioButton === 'limit') {
        // For limit orders: use totalPrice if manually entered, otherwise quantity * limit price
        userAmount = parseFloat(totalPrice || 0) || (parseFloat(quantity || 0) * parseFloat(priceLimit || 0))
      } else {
        // For market orders: prioritize manually entered price over auto-calculated convertedPrice
        userAmount = parseFloat(price || convertedPrice || 0)
      }
    } else {
      if (selectedRadioButton === 'limit') {
        // For limit orders: use totalPrice if manually entered, otherwise quantity * limit price
        userAmount = parseFloat(totalPrice || 0) || (parseFloat(quantity || 0) * parseFloat(priceLimit || 0))
      } else {
        // For market orders: prioritize manually entered price over auto-calculated convertedPrice
        userAmount = parseFloat(price || convertedPrice || 0)
      }
    }

    const feeAmount = (userAmount * parseFloat(fee.fee_percentage)) / 100
    return feeAmount.toFixed(8).replace(/\.?0+$/, '');
  }

  // Fee calculation function
  const calculateFeeWithValue = async () => {
    if (!activeMarketId) {
      console.log('❌ No active market, aborting fee calculation')
      return
    }

    setFeeLoading(true)
    try {
      const marketId = activeMarketId
      let price = 0

      if (selectedRadioButton === 'limit') {
        price = parseFloat(priceLimit || 0)
      } else {
        const marketPrice = parseFloat(marketTickers[marketId]?.last || 0)
        if (marketPrice <= 0) {
          const marketData = marketsList.find(el => el.id === marketId)
          const fallbackPrice = parseFloat(marketData?.last || 0)
          if (fallbackPrice <= 0) {
            console.log('❌ No market price available')
            setFee(null)
            return
          }
          price = fallbackPrice
        } else {
          price = marketPrice
        }
      }

      if (price <= 0) {
        console.log('❌ Invalid price:', price)
        setFee(null)
        return
      }

      const actualVolume = quantity || (price ? convertedPrice : null) || '1'

      const requestBody = {
        market_id: marketId,
        ord_type: selectedRadioButton,
        price: price.toString(),
        side: selectedTab,
        volume: actualVolume,
        actual_volume: actualVolume
      }

      const response = await API.post('trading/market/orders/new/estimate_fee', requestBody)

      const data = response.data
      console.log('✅ Fee response:', data)
      setFee(data)
    } catch (error) {
      console.error('❌ Error fetching fee:', error)
    } finally {
      setFeeLoading(false)
    }
  }

  // Debounced fee calculation
  const debouncedCalculateFee = () => {
    console.log('🕒 Debouncing fee calculation for 1.5 seconds...')

    if (feeCalculationTimer) {
      clearTimeout(feeCalculationTimer)
    }

    setFeeLoading(true)

    const newTimer = setTimeout(() => {
      console.log('✅ Debounce timer completed, calling fee calculation')
      calculateFeeWithValue()
    }, 1500)

    setFeeCalculationTimer(newTimer)
  }

  // useEffect(() => {
  //   marketTickers['btcusd']?.avg_price
  // }, [marketTickers])

  useEffect(() => {
    if (selectCurrencyModalData?.selectedMarket) {
      // Update currencies when a market is selected from the modal
      setFromCurrency(selectCurrencyModalData.selectedMarket.base_unit)
      setToCurrency(selectCurrencyModalData.selectedMarket.quote_unit)
    } else if (selectCurrencyModalData?.defaultSelectedMarket) {
      setFromCurrency(selectCurrencyModalData.defaultSelectedMarket.base_unit)
      setToCurrency(selectCurrencyModalData.defaultSelectedMarket.quote_unit)
    } else {
      setFromCurrency(marketsList?.find(el => el.id === selectedCoin.id)?.base_unit)
      setToCurrency(marketsList?.find(el => el.id === selectedCoin.id)?.quote_unit)
    }
  }, [selectedCoin, selectCurrencyModalData?.defaultSelectedMarket, selectCurrencyModalData?.selectedMarket])

  const handleQuantityChange = event => {
    setTotalPrice('')
    setQuantity(event.target.value)
    // Clear price to allow auto-calculation when user changes quantity
    setPrice('')

    // Trigger fee calculation for market orders or limit orders with price
    if (selectedRadioButton === 'market' && event.target.value && parseFloat(event.target.value) > 0) {
      debouncedCalculateFee()
    } else if (selectedRadioButton === 'limit' && event.target.value && parseFloat(event.target.value) > 0 && priceLimit && parseFloat(priceLimit) > 0) {
      debouncedCalculateFee()
    }
  }

  // const handlePriceChange = (event) => {
  //   setPrice(event.target.value)
  //   setQuantity("")
  // }

  const handlePriceChange = event => {
    setQuantity('')
    if (selectedRadioButton === 'limit') {
      setTotalPrice(event.target.value)
      // Trigger fee calculation for limit orders when total price is set
      if (event.target.value && parseFloat(event.target.value) > 0 && priceLimit && parseFloat(priceLimit) > 0) {
        debouncedCalculateFee()
      }
    } else {
      setPrice(event.target.value)
      // Trigger fee calculation for market orders when price is set
      if (event.target.value && parseFloat(event.target.value) > 0) {
        debouncedCalculateFee()
      }
    }
  }

  const handlePriceLimitChange = event => {
    // setTotalPrice("")
    setPriceLimit(event.target.value)
  }

  useEffect(() => {
    _getCurrencies(
      data => {
        setApiCurrencies(data)
      },
      error => {
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
            severity: 'error',
            //buttonMessage: 'See order',
            callback: () => { },
            //actionType: 'link', // link - close
            //linkTo: '/account/orders/open',
            position: 'topRight'
          })
        )
      }
    )
  }, [])

  let singleConvertedPrice = estimateUnitValue(toCurrency, fromCurrency, 1, currencies, marketsList, marketTickers)
  useEffect(() => {
    if (selectedRadioButton === 'limit') {
      // For limit orders, calculate based on totalPrice and priceLimit
      if (totalPrice && priceLimit) {
        setConvertedPrice(totalPrice / priceLimit)
      } else {
        setConvertedPrice('')
      }
    } else {
      // For market orders, calculate based on quantity or price
      // Always prioritize quantity calculation over price
      if (quantity && parseFloat(quantity) > 0) {
        // User entered quantity, calculate total price
        const calculatedPrice = estimateUnitValue(
          toCurrency,
          fromCurrency,
          parseFloat(quantity),
          currencies,
          marketsList,
          marketTickers
        )
        setConvertedPrice(calculatedPrice)
      } else if (price && parseFloat(price) > 0) {
        // User entered price (and no quantity), calculate quantity
        const calculatedQuantity = estimateUnitValue(
          fromCurrency,
          toCurrency,
          parseFloat(price),
          currencies,
          marketsList,
          marketTickers
        )
        setConvertedPrice(calculatedQuantity)
      } else {
        setConvertedPrice('')
      }
    }
    singleConvertedPrice = estimateUnitValue(toCurrency, fromCurrency, 1, currencies, marketsList, marketTickers)
  }, [marketTickers, quantity, price, totalPrice, priceLimit, selectedRadioButton, currencies, marketsList, toCurrency, fromCurrency])

  useEffect(() => {
    if (isLoggedIn) {
      _getBalances(
        res => {
          dispatch(setBalances(res))
        },
        () => { }
      )
    }
  }, [refreshBalance, activeMarketId])

  // Drop the previous market's fee when the pair genuinely changes, so a stale
  // allowed_to_trade is neither displayed nor validated against. Guarded by a ref
  // because activeMarketId is briefly undefined while marketsList reloads, and
  // clearing on that transition wipes a fee that was just fetched.
  const prevMarketIdRef = useRef(activeMarketId)
  useEffect(() => {
    if (!activeMarketId) return
    if (prevMarketIdRef.current && prevMarketIdRef.current !== activeMarketId) {
      setFee(null)
    }
    prevMarketIdRef.current = activeMarketId
  }, [activeMarketId])

  // Populate allowed_to_trade before the user types anything. calculateFeeWithValue
  // falls back to volume '1' when the inputs are empty, which is what the exchange
  // form does on mount. selectedTab is a dependency because `side` is part of the
  // request and the allowance differs between buy and sell.
  // refreshBalance is a dependency so the allowance is re-fetched after an order
  // fills, rather than showing the pre-trade figure.
  useEffect(() => {
    if (!activeMarketId || !isLoggedIn) return
    calculateFeeWithValue()
  }, [activeMarketId, isLoggedIn, selectedTab, selectedRadioButton, refreshBalance])

  // Recalculate as the amount changes for market orders.
  useEffect(() => {
    if (selectedRadioButton === 'market' && convertedPrice && parseFloat(convertedPrice) > 0) {
      debouncedCalculateFee()
    }
  }, [convertedPrice, selectedRadioButton, activeMarketId])

  const formatVolumeToMarketPrecision = (volume, marketId) => {
    if (!volume || !marketId) return volume
    const market = marketsList.find(el => el.id === marketId)
    if (!market) return volume
    const precision = market.step_size || market.amount_precision || 8
    const factor = Math.pow(10, precision)
    return (Math.floor(parseFloat(volume) * factor) / factor).toFixed(precision)
  }

  // Clear the form after a filled order. The pair, side and order type are left
  // alone so a follow-up trade on the same market does not have to be set up again.
  const resetInputValues = () => {
    setQuantity('')
    setPrice('')
    setPriceLimit('')
    setTotalPrice('')
    setConvertedPrice('')
    setSubmitted(false)
  }

  const handleSubmit = () => {
    setSubmitted(true)

    // Never submit against a market that could not be resolved from the displayed
    // pair — that is how a SOL/EUR order previously went out as btcusd.
    if (!activeMarketId) {
      dispatch(setSnackbarOpen(true))
      dispatch(
        setSnackbarData({
          alertMessage: 'This trading pair is unavailable. Please reselect the pair.',
          severity: 'error',
          callback: () => { },
          position: 'topRight'
        })
      )
      return
    }

    if (parseFloat(quantity || convertedPrice ? quantity || convertedPrice : '') < parseFloat(activeMarket?.min_amount)) {
      // If there's a form error, prevent the API call and show the error
      // console.log("Form submission prevented due to errors.")
      return
    }
    const rawVolume = price ? convertedPrice : quantity
    const formattedVolume = formatVolumeToMarketPrecision(rawVolume, activeMarketId)
    setButtonLoading(true)
    _addOrder(
      {
        market: activeMarketId,
        side: selectedTab,
        volume: formattedVolume,
        // Only include price property if selectedRadioButton is "limit"
        ...(selectedRadioButton === 'limit' && {
          price: priceLimit ? priceLimit : convertedPrice
        }),
        ord_type: selectedRadioButton
      },
      data => {
        setButtonLoading(false)
        dispatch(
          setSnackbarData({
            alertMessage: 'Order set',
            buttonMessage: 'See order',
            callback: () => { },
            actionType: 'link', // link - close
            linkTo: '/account/orders/open',
            severity: 'success',
            position: 'topRight'
          })
        )
        dispatch(setSnackbarOpen(true))
        dispatch(startPolling()) // Start polling for order updates
        resetInputValues()
        setRefreshBalance(!refreshBalance)
        // The order consumed balance, so the previous allowed_to_trade is stale.
        setFee(null)
      },
      error => {
        setButtonLoading(false)
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
            severity: 'error',
            //buttonMessage: 'See order',
            callback: () => { },
            //actionType: 'link', // link - close
            //linkTo: '/account/orders/open',
            position: 'topRight'
          })
        )
        setRefreshBalance(!refreshBalance)
      }
    )
  }

  return (
    <div
      className={`${Styles.card} ${selectedRadioButton === 'market' ? Styles.cardMarket : Styles.cardLimit}`}
      style={{ ...containerStyle }}
    >
      {!hideTitle && <h5 style={{ marginBottom: 20 }}>QUICK TRADE</h5>}

      {/* tabs container */}
      <div className={Styles.buySellTabsContainer}>
        <button
          className={selectedTab === 'buy' ? Styles.selectedTab : undefined}
          onClick={() => {
            if (selectedTab === 'buy') return
            setSelectedTab('buy')
          }}
        >
          <FormattedMessage id='page.markets.trade.buy' /> {fromCurrency?.toUpperCase()}
        </button>
        <button
          className={selectedTab === 'sell' ? Styles.selectedTab : undefined}
          onClick={() => {
            if (selectedTab === 'sell') return
            setSelectedTab('sell')
          }}
        >
          <FormattedMessage id='page.markets.trade.sell' /> {fromCurrency?.toUpperCase()}
        </button>
      </div>

      {/* tab content */}
      <div className={Styles.tabContentContainer}>
        <div className={Styles.formFieldsContainer}>
          {/* radio buttons container */}
          <div className={Styles.radioButtonContainer}>
            <RadioButton
              text={<FormattedMessage id='page.markets.trade.market' />}
              icon={<MarketCapIconFilled width={12} />}
              isSelected={selectedRadioButton === 'market'}
              onClick={() => {
                if (selectedRadioButton === 'market') return
                setSelectedRadioButton('market')
              }}
            />
            <RadioButton
              text={<FormattedMessage id='page.markets.trade.limit' />}
              icon={<FlagIcon />}
              isSelected={selectedRadioButton === 'limit'}
              onClick={() => {
                if (selectedRadioButton === 'limit') return
                setSelectedRadioButton('limit')
              }}
            />
          </div>

          {/* COIN PRICE / SELECT PAIR ROW */}
          <div className={Styles.coinPriceSelectPairContainer}>
            <div className={Styles.approxPrice}>
              <div>
                <img
                  src={apiCurrencies ? apiCurrencies?.find(el => el.id === fromCurrency?.toLowerCase())?.icon_url : '/'}
                />
                <span>{apiCurrencies ? apiCurrencies?.find(el => el.id === fromCurrency?.toLowerCase())?.name : ''}</span>
                {apiCurrencies && apiCurrencies?.find(el => el.id === fromCurrency?.toLowerCase())?.id.toUpperCase() ?
                  <CoinTag
                    size={10}
                    tag={apiCurrencies?.find(el => el.id === fromCurrency?.toLowerCase())?.id.toUpperCase()}
                  />
                  : null}
              </div>

              <div>
                {' '}
                &#8776; {formatNumber(singleConvertedPrice)} {toCurrency?.toUpperCase()}
              </div>
            </div>

            {/* SELECT PAIRS */}
            <div>
              <Button
                text='Select Pair'
                variant='link'
                style={{
                  color: 'var(--gray-color)',
                  paddingRight: 10
                }}
                icon={<DownArrowIcon />}
                iconPosition='right'
                onClick={() => {
                  dispatch(
                    setSelectCurrencyModalData({
                      title: 'Select Pair For Quick Trade',
                      showTabs: false,
                      type: 'all',
                      showMarketPairs: true,
                      searchPlaceholder: 'Search pair',
                      currenciesToHide: [toCurrency],
                      disableBackdropClick: false,
                      callback: market => {
                        setFromCurrency(market.base_unit)
                        setToCurrency(market.quote_unit)
                      }
                    })
                  )
                  dispatch(setSelectCurrencyModalOpen(true))
                }}
              />
            </div>
          </div>
          {selectedRadioButton === 'limit' ? (
            <ModernCurrencyInput
              type='number'
              title={`Price Limit`}
              placeholder='0.00'
              onChange={handlePriceLimitChange}
              containerStyle={{
                marginBottom: 20,
                marginTop: 15
              }}
              coinName={toCurrency?.toUpperCase()}
              currencyImg={apiCurrencies ? apiCurrencies?.find(el => el.id === toCurrency?.toLowerCase())?.icon_url : ''}
              hint='Want to buy or sell at a specific price?'
            />
          ) : null}

          <ModernCurrencyInput
            type='number'
            title={`Amount`}
            placeholder='0.00'
            value={quantity || convertedPrice ? quantity || convertedPrice : ''}
            onChange={handleQuantityChange}
            containerStyle={{
              marginBottom: 0,
              marginTop: selectedRadioButton === 'market' ? 25 : 15
            }}
            error={
              (submitted && parseFloat(quantity || convertedPrice ? quantity || convertedPrice : '') < parseFloat(activeMarket?.min_amount))
                ? { message: `Minimum deposit amount is ${activeMarket?.min_amount}` }
                : (isAmountExceedingBalance && (quantity || convertedPrice))
                  ? {
                    message: `Amount exceeds available balance: ${balances?.find(el => el.currency === (selectedTab === 'buy' ? toCurrency : fromCurrency))?.balance || 0} ${selectedTab === 'buy' ? toCurrency?.toUpperCase() : fromCurrency?.toUpperCase()}`
                  }
                  : isAmountExceedsAllowedLimit
                    ? {
                      message: `Amount exceeds allowed limit of ${formatToTenDecimals(getAllowedToTrade() ?? 0)} ${selectedTab === 'buy' ? toCurrency?.toUpperCase() : fromCurrency?.toUpperCase()}`
                    }
                    : undefined
            }
            coinName={fromCurrency?.toUpperCase()}
            currencyImg={apiCurrencies ? apiCurrencies?.find(el => el.id === fromCurrency?.toLowerCase())?.icon_url : ''}
            hint={`How much ${fromCurrency?.toUpperCase()} do you want to ${selectedTab}?`}
          />

          {/* Balance display */}
          {userData.email && (
            <div style={{
              marginTop: 10,
              marginBottom: 15,
              fontSize: '0.85rem',
              color: 'var(--gray-color)'
            }}>
              Available balance: {parseFloat(balances?.find(el => el.currency === (selectedTab === 'buy' ? toCurrency : fromCurrency))?.balance ?? 0)} {selectedTab === 'buy' ? toCurrency?.toUpperCase() : fromCurrency?.toUpperCase()}
            </div>
          )}

          <ModernCurrencyInput
            type='number'
            title={selectedTab === 'buy' ? 'Buy For' : 'Sell For'}
            placeholder='0.00'
            value={
              selectedRadioButton === 'limit'
                ? totalPrice
                  ? totalPrice
                  : convertedPrice || priceLimit * quantity
                    ? convertedPrice || priceLimit * quantity
                    : ''
                : price || convertedPrice
                  ? price || convertedPrice
                  : ''
            }
            onChange={handlePriceChange}
            containerStyle={{
              marginBottom: selectedRadioButton === 'market' ? 20 : 10,
              marginTop: 15
            }}
            onDropdownClick={() => { }}
            coinName={toCurrency?.toUpperCase()}
            currencyImg={apiCurrencies ? apiCurrencies?.find(el => el.id === toCurrency?.toLowerCase())?.icon_url : ''}
            hint={`Your calculated total ${selectedTab === 'buy' ? 'spend' : 'gain'}, based off your request`}
          />
        </div>

        {/* fee info */}
        {isLoggedIn && (
          <div className={Styles.feesContainer}>
            <div className={Styles.feesInfoContainer}>
              <span className={Styles.feesInfoRow}>
                <InfoIcon color={'var(--gray-color)'} width={12} />
                Fees to trade:{' '}
              </span>
              <span style={{ color: 'var(--gray-color)' }}>
                {feeLoading ? (
                  <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Calculating...</span>
                ) : fee ? (
                  `${calculateActualFee()} ${toCurrency?.toUpperCase()} (${fee.fee_percentage}%)`
                ) : (
                  `0.00 ${toCurrency?.toUpperCase()} (0%)`
                )}
              </span>
            </div>
            <div className={Styles.feesInfoContainer}>
              <span className={Styles.feesInfoRow}>
                <InfoIcon color={'var(--gray-color)'} width={12} />
                Allowed to {selectedTab}:{' '}
              </span>
              <span style={{ color: 'var(--gray-color)', marginTop: '5px' }}>
                {feeLoading ? (
                  <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Calculating...</span>
                ) : (
                  (() => {
                    // Buy is capped in the quote currency, sell in the base currency.
                    // Reading the base currency for a buy showed the (usually zero)
                    // balance of the coin being bought.
                    const allowedCurrency = selectedTab === 'buy' ? toCurrency : fromCurrency
                    const allowedAmount = getAllowedToTrade()
                    if (allowedAmount === null) return `≈ 0 ${allowedCurrency?.toUpperCase()}`
                    return (
                      <span
                        title={`${allowedAmount} ${allowedCurrency?.toUpperCase()} allowed to trade (from estimate_fee)`}
                        style={{ cursor: 'help' }}
                      >
                        {`≈ ${formatToTenDecimals(allowedAmount)} ${allowedCurrency?.toUpperCase()}`}
                      </span>
                    )
                  })()
                )}
              </span>
            </div>
          </div>
        )}

        {/* buy/sell button */}
        <div className={Styles.buttonContainer}>
          {userData.email ? (
            <Button
              text={`${selectedTab === 'buy' ? 'Buy' : 'Sell'} ${fromCurrency?.toUpperCase()}`}
              style={{
                marginTop: 0,
                backgroundColor: selectedTab === 'sell'
                  ? (isOrderBlocked ? '#6b7280' : 'rgba(240,68,68,255)')
                  : (isOrderBlocked ? '#6b7280' : '#10B981'),
                color: 'white'
              }}
              onClick={e => {
                if (!isOrderBlocked) {
                  handleSubmit()
                }
              }}
              disabled={isOrderBlocked}
              isLoading={buttonLoading}
            />
          ) : (
            <Button type='loggedOut' />
          )}
        </div>
      </div>
    </div >
  )
}

export default QuickBuySell
